import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {LoginSignupPromoCarousel} from '@Components/login-signup-promo-carousel/login-signup-promo-carousel';
import {NotificationBanner} from '@Components/login-page/components/notification-banner';
import {CardMessage, CardMessageType} from '@Components/login-page/components/card-message';
import {LoginPageContent} from '@Components/login-page/components/login-page-content';
import type {LOGIN_OPTIONS} from '@Libraries/login-signup-library';
import {AUTH_TYPE, doesAuthTypeNeedBackButton, getOAuthClientMessage, initializeAuthFormParams, initializeoAuthFormParams, isMobileScreen, isOAuthFlow} from '@Libraries/login-signup-library';
import type {SavedLoginAccount} from '@Libraries/login-signup-library';
import type {CommonAuthFormParams, LoginPageInitializeProps, OauthFormBaseParams} from '@Components/login-page/login-page.types';
import {useIsLoginToProceedMessage, useLoginPageAnimateNotificationBanner, useLoginPageAuthType, useLoginPageErrorMessage, useLoginPageShowError} from '@Components/login-page/login-page.hooks';
import {initializeLoginPageState} from './login-page-reducer';
import {useAppDispatch} from '@/hooks';
import useWindowSize from '@/hooks/useWindowSize';
import {useOnRecaptchaRender} from '@/hooks/recaptcha/useOnRecaptchaRender';
import styles from './login-page.module.scss';
import {trackGA4EventForLoginClick} from '@Panels/share-panel/share-panel-library';

export interface LoginPageProps {
  loginOptionsMode: LOGIN_OPTIONS;
  loginActionType: string;
  signupActionType: string;
  isInDataRegulatedLocation: boolean;
  referrer?: string;
  teamName?: string;
  encryptedTeamName?: string;
  idTeam?: string;
  invite?: string;
  redirectUrl?: string;
  clientName?: string;
  clientId?: string;
  responseType?: string;
  redirectUri?: string;
  state?: string;
  message?: string;
  showErrorMessage?: boolean;
  openForgotPasswordPanel?: boolean;
  /** Unsealed by the server from the httpOnly cookie and rendered into the page (§32); empty on the D60 bypass arrivals. */
  savedLoginAccounts?: SavedLoginAccount[];
}

export function LoginPage({
  loginOptionsMode,
  loginActionType,
  signupActionType,
  isInDataRegulatedLocation,
  referrer = '',
  teamName = '',
  encryptedTeamName = '',
  idTeam = '',
  invite = '',
  redirectUrl = '',
  clientName = '',
  clientId = '',
  responseType = '',
  redirectUri = '',
  state = '',
  message = '',
  showErrorMessage = false,
  openForgotPasswordPanel = false,
  savedLoginAccounts = [],
}: LoginPageProps): ReactElement<any> {
  const [pageAnimationClass, setPageAnimationClass] = useState('');

  const dispatch = useAppDispatch();
  const type = useLoginPageAuthType();
  const showError = useLoginPageShowError();
  const errorMessage = useLoginPageErrorMessage();
  const isLoginToProceedMessage = useIsLoginToProceedMessage();
  const animateBanner = useLoginPageAnimateNotificationBanner();
  const windowScreenSize = useWindowSize();

  const pageRef = useRef<HTMLInputElement>(null);

  useOnRecaptchaRender();

  useEffect(() => {
    const initPageProps: LoginPageInitializeProps = {
      errorMessage: message,
      showError: showErrorMessage,
      isInDataRegulatedLocation: isInDataRegulatedLocation ?? false,
      signupActionType,
      loginActionType,
      redirectUrl,
      isLoginToProceedMessage: showErrorMessage,
      isOAuthFlow: isOAuthFlow(loginOptionsMode),
      initialAuthType: getInitialAuthType(),
      savedAccounts: savedLoginAccounts,
    };

    dispatch(initializeLoginPageState(initPageProps));

    const authFormParams: CommonAuthFormParams = {
      teamName: encryptedTeamName,
      idTeam,
      invite,
      redirecturl: redirectUrl,
      loginActionType,
      signupActionType,
    };

    initializeAuthFormParams(authFormParams);

    const oauthFormParams: OauthFormBaseParams = {
      clientId,
      clientName,
      responseType,
      redirectUri,
      state,
    };
    initializeoAuthFormParams(oauthFormParams);
    trackCollaborationGA4Events();
  }, []);

  useEffect(() => {
    if (animateBanner) {
      scrollToTop();
    }
  }, [animateBanner]);

  /**
   * Pages that can only offer "reset your password" as the next step link straight into that panel,
   * rather than dropping the user on the login form to find it.
   */
  const getInitialAuthType = (): AUTH_TYPE | undefined => {
    return openForgotPasswordPanel ? AUTH_TYPE.FORGOT_PASSWORD : undefined;
  };

  const trackCollaborationGA4Events = (): void => {
    trackGA4EventForLoginClick(redirectUrl);
  };

  const onAuthTypeChange = (): void => {
    scrollToTop();
    setPageAnimationClass(styles.pageAnimation);
  };

  const isUserJoiningTeam = (): boolean => {
    return idTeam !== '' && invite !== '' && teamName !== '';
  };

  const scrollToTop = (): void => {
    pageRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const doesReferrerExist = (): boolean => {
    return referrer !== undefined && referrer !== '';
  };

  const enablePageAnimation = (): void => {
    setPageAnimationClass(styles.pageAnimation);
  };

  const isOAuthClientMessageShown = (): boolean => {
    return isOAuthFlow(loginOptionsMode) && !showError;
  };

  /**
   * Below the tablet breakpoint every notice the floating banner would carry becomes a strip across the top
   * of the card instead, so the card gives up its top padding and the strip supplies its own.
   */
  const isMessageShownInsideCard = (): boolean => {
    return isMobileScreen(windowScreenSize.windowWidth) && (showError || isOAuthClientMessageShown());
  };

  const hasBackControlHeading = (): boolean => {
    return doesAuthTypeNeedBackButton(type) || type === AUTH_TYPE.SIGNUP_PASSWORD || type === AUTH_TYPE.SAVED_ACCOUNT_PASSWORD;
  };

  /** The card trims its top padding on the panels whose heading carries the back control. */
  const getCardTopPaddingClass = (): string => {
    if (isMessageShownInsideCard()) {
      return styles.formWithCardMessage;
    }
    return hasBackControlHeading() ? styles.formWithBackControlHeading : '';
  };

  const getClasses = (): string => {
    return `${styles.form} ${getCardTopPaddingClass()} flex-column-align-center ${pageAnimationClass}`;
  };

  /** Mirrors the banner's own precedence: a real failure takes the panel over from the OAuth context line. */
  const getCardMessage = (): ReactElement | null => {
    if (!isMessageShownInsideCard()) {
      return null;
    }
    if (isOAuthClientMessageShown()) {
      return <CardMessage message={getOAuthClientMessage(type, clientName)} type={CardMessageType.INFO} />;
    }
    return <CardMessage message={errorMessage} type={isLoginToProceedMessage ? CardMessageType.INFO : CardMessageType.ERROR} />;
  };

  const getFloatingNotificationBanner = (): ReactElement | null => {
    if (isMessageShownInsideCard()) {
      return null;
    }
    return <NotificationBanner showOauthClientMessage={isOAuthFlow(loginOptionsMode)} clientName={clientName} />;
  };

  /** The OAuth page renders without the site nav (`hideHeader`), so it must not reserve the nav's height. */
  const getPageContainerClasses = (): string => {
    return isOAuthFlow(loginOptionsMode) ? `${styles.loginPageContainer} ${styles.loginPageWithoutNav}` : styles.loginPageContainer;
  };

  /** Reserves room for the floating notification banner without moving the form. */
  const getErrorSpacingClass = (): string => {
    return showError ? styles.errorVisible : '';
  };

  const getLoginPageContent = (): ReactElement => {
    return (
      <>
        {getFloatingNotificationBanner()}
        <div
          className={getClasses()}
          onAnimationEnd={() => {
            setPageAnimationClass('');
          }}
        >
          {getCardMessage()}
          <LoginPageContent
            onAuthTypeChangeCallback={onAuthTypeChange}
            onPanelChange={enablePageAnimation}
            loginOptionsMode={loginOptionsMode}
            isUserJoiningTeam={isUserJoiningTeam()}
            teamName={teamName}
            redirectUrl={redirectUrl}
          />
        </div>
      </>
    );
  };

  return (
    <div className={`${getPageContainerClasses()} flex-h-row`}>
      <div className={styles.testimony}>
        <LoginSignupPromoCarousel doesReferrerExist={doesReferrerExist()} />
      </div>
      <div className={`${styles.formContainer} ${getErrorSpacingClass()} js-login-page-form-container flex-v-row flex-1`} ref={pageRef}>
        {getLoginPageContent()}
      </div>
    </div>
  );
}
